import { tTemplates } from 'src/pages/Pipe/Pipe.types.ts';
import { tComponent } from 'src/pages/WhatsappApi/WhatsappApi.types.ts';
import { eComponentType } from 'src/pages/WhatsappApi/WhatsappApi.enum.ts';
import {
  tCampaignRecipientPreview,
  tCreditRejection,
} from 'src/pages/Pipe/modals/BulkMessageWABModal/BulkMessage.types.ts';

/** Narrow an unknown payload value to a finite number, else undefined. */
function asFiniteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
}

/**
 * Defensive parser for the WBS recipient-preview payload (BUG-569).
 *
 * The endpoint ships from whatsapp-business-service separately from this FE.
 * WBS wraps every response in its standard `{ message, code, result: {...} }`
 * envelope, so the payload lives under `result`; we also tolerate a legacy
 * `{ data: ... }` wrapper and a bare top-level body for mixed deploys, the
 * `recipients` alias instead of `requested`, and omitted numeric fields.
 * Returns null whenever no usable recipient count is present — the caller then
 * falls back to the stage counter and flags the count as stale.
 */
export function parseRecipientPreview(
  raw: unknown,
): tCampaignRecipientPreview | null {
  if (!raw || typeof raw !== 'object') return null;

  const candidate = raw as Record<string, unknown>;
  const envelope =
    candidate.result && typeof candidate.result === 'object'
      ? candidate.result
      : candidate.data && typeof candidate.data === 'object'
        ? candidate.data
        : candidate;
  const body = envelope as Record<string, unknown>;

  const requested =
    asFiniteNumber(body.requested) ?? asFiniteNumber(body.recipients);
  if (requested === undefined) return null;

  return {
    requested,
    available: asFiniteNumber(body.available),
    limit: asFiniteNumber(body.limit),
    used: asFiniteNumber(body.used),
    pendingReserved: asFiniteNumber(body.pending_reserved),
    unlimited: body.unlimited === true || body.not_provisioned === true,
  };
}

/**
 * Detects a wba-10024 credit rejection on a campaign-create error (BUG-569).
 *
 * Status-agnostic by design: the new WBS rejects with HTTP 402 and a
 * `{ code, error, message, requested, available, ... }` body, while the
 * legacy controllers reject with HTTP 500 and `{ error, message }` only —
 * both must resolve here, so only the body's `code ?? error` is inspected.
 * Returns null for any non-credit error; numbers are undefined when the
 * payload doesn't carry them (legacy body) so the caller renders generic
 * copy instead of interpolating `undefined`.
 */
export function parseCreditRejection(error: unknown): tCreditRejection | null {
  const data = (
    error as { response?: { data?: unknown } } | null | undefined
  )?.response?.data;
  if (!data || typeof data !== 'object') return null;

  const body = data as Record<string, unknown>;
  const code = body.code ?? body.error;
  if (code !== 'wba-10024') return null;

  return {
    requested: asFiniteNumber(body.requested),
    available: asFiniteNumber(body.available),
  };
}

// WA Business API variable names are alphanumeric in practice, but `new RegExp`
// would throw a SyntaxError (uncaught by the JSON.parse try/catch below) if a
// name ever contained a regex metacharacter. Escape defensively.
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Returns the length of a template's BODY component after variable
 * substitution (hydration). Variables without a value stay as their
 * literal {{name}} placeholder so unfilled templates report the raw-body
 * length as a lower bound — matching the preview logic in
 * SelectTemplate.hook.tsx. Meta rejects hydrated bodies over 1024 chars
 * with "Hydrated text too long", which is what this enables the UI to
 * pre-empt.
 */
export function computeHydratedBodyLength(
  selectedTemplate: tTemplates | null,
  paramValues: Record<string, string>,
): number {
  if (!selectedTemplate?.components) return 0;

  let components: tComponent[];
  try {
    components = JSON.parse(selectedTemplate.components) as tComponent[];
  } catch {
    return 0;
  }

  const bodyText = components.find(
    (component) => component.type === eComponentType.BODY,
  )?.text;
  if (!bodyText) return 0;

  let hydrated = bodyText;
  Object.entries(paramValues).forEach(([name, value]) => {
    if (!value) return;
    const pattern = new RegExp(`\\{\\{${escapeRegExp(name)}\\}\\}`, 'g');
    hydrated = hydrated.replace(pattern, value);
  });

  return hydrated.length;
}
